import React from "react";
import "./WaveDividerVertical.scss";

interface WaveDividerVerticalProps {
  settings: {
    wave_width: number;
    wave_color: string;
    wave_opacity: number;
    wave_style: 'wavy' | 'jagged' | 'curved' | 'straight' | 'single-curve';
    wave_offset: number;
    flip_vertically: boolean;
    flip_horizontally: boolean;
    hide_on_mobile: boolean;
    z_index: number;
    position: 'left' | 'right' | 'both';
    render_type?: 'all' | 'mobile-only' | 'desktop-only';
    custom_id?: string;
    custom_class?: string;
    show_section_borders?: boolean;
    background_color?: string;
  };
}

export const WaveDividerVertical = ({ settings }: WaveDividerVerticalProps) => {
  const {
    wave_width = 60,
    wave_color = "#ffffff",
    wave_opacity = 1,
    wave_offset,
    wave_style = 'wavy',
    flip_vertically = false,
    flip_horizontally = false,
    hide_on_mobile = false,
    z_index = 1,
    position = 'right',
    render_type = 'all',
    custom_id,
    custom_class,
    show_section_borders = false,
    background_color,
  } = settings;

  const getWavePath = (style: string) => {
    switch (style) {
      case 'wavy':
        return "M40,0 C0,150 80,250 40,400 C0,550 80,650 40,800 L100,800 L100,0 Z";
      case 'jagged':
        return "M50,0 L0,100 L50,200 L0,300 L50,400 L0,500 L50,600 L0,700 L50,800 L100,800 L100,0 Z";
      case 'curved':
        return "M40,0 Q80,200 40,400 Q0,600 40,800 L100,800 L100,0 Z";
      case 'single-curve':
        return "M100,0 L100,800 L0,800 C60,600 60,200 0,0 Z";
      case 'reverse-curve':
        return "M0,0 L0,800 L100,800 C40,600 40,200 100,0 Z";
      case 'straight':
      default:
        return "M40,0 L40,800 L100,800 L100,0 Z";
    }
  };

  const getTransforms = () => {
    let transforms = [];
    if (flip_horizontally) transforms.push("scaleX(-1)");
    if (flip_vertically) transforms.push("scaleY(-1)");
    return transforms.join(" ");
  };

  const renderWave = (pos: string) => {
    const isVisible = position === 'both' || position === pos;
    if (!isVisible) return null;

    const transformClass = pos === 'left' ? 'wave-divider-left' : '';

    return (
      <div
        className={`vertical-wave-divider ${transformClass} ${hide_on_mobile ? 'hidden-mobile' : ''}`}
        style={{
          width: `${wave_width}px`,
          zIndex: z_index,
          transform: getTransforms(),
          position: 'absolute',
          [pos]: 0,
          top: 0,
          bottom: 0,
          left: pos === 'left' ? `${wave_offset}px` : undefined,
          right: pos === 'right' ? `${wave_offset}px` : undefined,
          overflow: 'hidden',
        }}
      >
        <svg
          preserveAspectRatio="none"
          viewBox="0 0 100 800"
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
          }}
        >
          <path
            d={getWavePath(wave_style)}
            fill={wave_color}
            fillOpacity={wave_opacity}
          />
        </svg>
      </div>
    );
  };

  const getContainerClasses = () => {
    let classes = ["vertical-wave-divider-container"];
    if (custom_class) classes.push(custom_class);
    if (show_section_borders) classes.push("section-border");
    return classes.join(" ");
  };

  return (
    <div
      id={custom_id}
      className={getContainerClasses()}
      style={{
        width: `${wave_width}px`,
        height: '100%',
        position: 'relative',
        backgroundColor: background_color || 'transparent',
      }}
    >
      {renderWave('left')}
      {renderWave('right')}
    </div>
  );
};
